import { macroCondition, isTesting } from '@embroider/macros';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import LpnCard from 'tio-common/components/lpn/lpn-card';
import pageTitle from 'ember-page-title/helpers/page-title';
import { PhGlobe, PhFaders, PhFileText } from 'ember-phosphor-icons';
import { VStack, Section } from 'tio-ui/components/layout';
import PageHero from 'tio-ui/components/page-hero';
import { t, type IntlService } from 'ember-intl';
import { Paginator } from 'tio-ui/components/paginator';
import type { LpnPageExtension as LpnPage } from 'tio-data/data/resources/lpn-page.ext';
import type { CompanySettingExtension as CompanySettingModel } from 'tio-data/data/resources/company-setting.ext';
import type AuthenticatedLpnIndexController from 'tio-employee/controllers/authenticated/lpn/index';
import { service } from '@ember/service';
import Drawer from 'tio-ui/components/drawer';
import Button from 'tio-ui/components/button';
import TooltipButton from 'tio-ui/components/tooltip-button';
import DiscountLetterModal from 'tio-employee/components/lpn/discount-letter-modal';
import SearchInput from 'tio-ui/components/search-input';
import LpnFilterFields, {
  type LpnFilterKey,
  type LpnFilterValues,
} from 'tio-employee/components/lpn/filter-fields';

interface LpnIndexComponentArgs {
  model: {
    recommendedPartners: LpnPage[];
    allPartners: LpnPage[];
    companySettings: CompanySettingModel;
    totalItems: number;
  };
  controller: AuthenticatedLpnIndexController;
}

export default class LpnIndexComponent extends Component<LpnIndexComponentArgs> {
  @service declare intl: IntlService;

  @tracked isFilterDrawerOpen = false;
  @tracked isDiscountLetterOpen = false;
  @tracked searchValue = this.args.controller.search ?? '';
  _searchTimer: ReturnType<typeof setTimeout> | null = null;

  static readonly SEARCH_DEBOUNCE_MS = macroCondition(isTesting()) ? 0 : 300;
  static readonly SEARCH_MIN_CHARS = 3;

  get activeFilterCount(): number {
    const c = this.args.controller;
    return (
      (c.savings?.length ?? 0) +
      (c.format?.length ?? 0) +
      (c.programs?.length ?? 0) +
      (c.areasOfStudy?.length ?? 0)
    );
  }

  get filterButtonLabel(): string {
    const count = this.activeFilterCount;
    return count > 0 ? `Filters (${count})` : 'Filters';
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  openDiscountLetter = () => {
    this.isDiscountLetterOpen = true;
  };

  closeDiscountLetter = () => {
    this.isDiscountLetterOpen = false;
  };

  changePaginatorPage = (page: number) => {
    this.args.controller.page = Math.max(1, page);
  };

  openFilterDrawer = () => {
    this.isFilterDrawerOpen = true;
  };

  closeFilterDrawer = () => {
    this.isFilterDrawerOpen = false;
  };

  handleSearchChange = (value: string) => {
    this.searchValue = value;

    if (this._searchTimer) {
      clearTimeout(this._searchTimer);
    }

    // If cleared or below threshold, reset immediately
    if (value.length === 0) {
      this.applySearch();
      return;
    }

    if (value.length < LpnIndexComponent.SEARCH_MIN_CHARS) {
      return;
    }

    this._searchTimer = setTimeout(() => {
      this.applySearch();
    }, LpnIndexComponent.SEARCH_DEBOUNCE_MS);
  };

  handleSearchClear = () => {
    this.searchValue = '';
    this.applySearch();
  };

  applySearch = () => {
    this.args.controller.search = this.searchValue;
    this.args.controller.page = 1;
  };

  get filterValues(): LpnFilterValues {
    const c = this.args.controller;
    return {
      savings: c.savings ?? [],
      format: c.format ?? [],
      programs: c.programs ?? [],
      areasOfStudy: c.areasOfStudy ?? [],
    };
  }

  setFilter = (key: LpnFilterKey, keys: string[]) => {
    this.args.controller[key] = keys;
    this.args.controller.page = 1;
  };

  clearAllFilters = () => {
    this.args.controller.savings = [];
    this.args.controller.format = [];
    this.args.controller.programs = [];
    this.args.controller.areasOfStudy = [];
    this.args.controller.page = 1;
  };

  <template>
    {{pageTitle (t "lpn.learning_partner_network")}}
    <VStack class="gap-8">
      <PageHero
        @title={{t "lpn.learning_partner_network"}}
        @subtitle={{if @model.allPartners.length (t "lpn.lpn_description")}}
        @icon={{PhGlobe}}
        @gradient="brand-soft"
        class="tio-elevation-card-raised"
      >
        <:actions>
          <SearchInput
            @placeholder={{t "lpn.filters.search_placeholder"}}
            @value={{this.searchValue}}
            @onChange={{this.handleSearchChange}}
            @onClear={{this.handleSearchClear}}
          />
          <Button
            @appearance="outlined"
            @label={{this.filterButtonLabel}}
            @icon={{PhFaders}}
            {{on "click" this.openFilterDrawer}}
          />
          <TooltipButton
            @appearance="outlined"
            @label={{t "lpn.discount_letter.open"}}
            @tooltip={{t "lpn.discount_letter.open_tooltip"}}
            @icon={{PhFileText}}
            {{on "click" this.openDiscountLetter}}
            data-test-lpn-discount-letter-open
          />
        </:actions>
      </PageHero>

      {{#if @model.recommendedPartners.length}}
        <Section>
          <:header>
            {{t "lpn.recommended"}}
          </:header>
          <:body>
            <div class="grid grid-cols-1 xl:grid-cols-3 gap-3">
              {{#each @model.recommendedPartners as |lpn|}}
                <LpnCard @lpn={{lpn}} @companySettings={{@model.companySettings}} />
              {{/each}}
            </div>
          </:body>
        </Section>
      {{/if}}

      <Section>
        <:header>
          {{t "lpn.schools_in_network"}}
        </:header>
        <:body>
          {{#if @model.allPartners.length}}
            <div class="grid grid-cols-1 xl:grid-cols-3 gap-3">
              {{#each @model.allPartners as |lpn|}}
                <LpnCard @lpn={{lpn}} @companySettings={{@model.companySettings}} />
              {{/each}}
            </div>
            <Paginator
              @totalItems={{@model.totalItems}}
              @pageSize={{@controller.limit}}
              @currentPage={{@controller.page}}
              @onChange={{this.changePaginatorPage}}
              class="my-4"
            />
            <p class="tio-text-xs-semibold tio-fg-muted">{{t "lpn.lpn_disclaimer_title"}}</p>
            <p class="tio-text-xs-normal tio-fg-muted">{{t "lpn.lpn_disclaimer_body"}}</p>
          {{else}}
            <p class="tio-fg-secondary">{{t "lpn.no_schools"}}</p>
          {{/if}}
        </:body>
      </Section>
    </VStack>

    {{#if this.isDiscountLetterOpen}}
      <DiscountLetterModal @source="lpn_page" @onClose={{this.closeDiscountLetter}} />
    {{/if}}

    <Drawer
      @isOpen={{this.isFilterDrawerOpen}}
      @onClose={{this.closeFilterDrawer}}
      @placement="right"
      @size="sm"
      @manageHistory={{false}}
    >
      <:header>
        <div class="tio-row-between w-full pr-8">
          <h2 class="tio-text-lg-semibold tio-fg-primary">{{t "lpn.filters.title"}}</h2>
          <button
            type="button"
            class="tio-text-sm-medium tio-fg-info hover:tio-fg-info-h"
            {{on "click" this.clearAllFilters}}
          >
            {{t "lpn.filters.clear_all"}}
          </button>
        </div>
      </:header>
      <:body>
        <LpnFilterFields @values={{this.filterValues}} @onChange={{this.setFilter}} />
      </:body>
    </Drawer>
  </template>
}
